import React, { FC, Fragment, useContext, useEffect, useState } from 'react';
import { CurrencyContext } from 'context/currency';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import MaterialsDataGrid from 'components/order-flow/lab-order/construction-step/construction-section/ations-and-materials/materials-data-grid/materials-data-grid.component';
import { useMutation, useQuery } from '@apollo/client';
import { materialPriceListForOrderTemplate } from 'apollo/templates/client-directory';
import { MaterialPriceList } from 'models/material';
import { Restoration } from 'models/order';
import { MaterialGridInput } from 'models/order-input/shared';
import { Alert, Box, Button } from '@mui/material';
import { isRealCurrency, shouldShowCurrencyMissingAlert } from 'utils/currency';
import { getGridCurrencyMismatch } from '../grid-currency-mismatch';
import { LoadingButton } from '@mui/lab';
import { useTranslation } from 'react-i18next';
import { updateRestorationTemplate } from 'apollo/templates/order';
import {
    isWarrantyRestoration,
    restorationActionsToGridInputs,
    restorationMaterialsToGridInputs,
    restorationWithGridRows,
} from 'components/order-flow/shared/restoration-input-helper';
import { OrderDetailsContext } from 'pages/order-details/order-details-context';
import { CONSTANTS } from 'config/constants';
import isEqual from 'lodash/isEqual';

type MaterialsDataGridWrapperProps = {
    /** The restoration whose material rows the grid edits. */
    restoration: Restoration;
    onEditComplete?: () => void;
};

/**
 * The materials grid on the order details page, for every order shape (DD-2506 step 3):
 * saving sends the restoration back through `updateRestoration` with its action rows as
 * they are; rows sent with their id keep it and the server prices the rest.
 */
const MaterialsDataGridWrapper: FC<MaterialsDataGridWrapperProps> = ({
    restoration,
    onEditComplete,
}: MaterialsDataGridWrapperProps) => {
    const { t: translate } = useTranslation(['common', 'orders']);
    const { currency, setCurrency } = useContext(CurrencyContext);
    // Shared page currency (see the actions grid): nothing to save the rows in when it is unset.
    const hasRealCurrency = isRealCurrency(currency);
    const { showBoundary } = useDenthubErrorBoundary();
    const { order } = useContext(OrderDetailsContext);
    const customerId = order.customer?._id as string;
    const warranty = isWarrantyRestoration(restoration) || Boolean(order.isWarrantyOrder);
    const [materialPriceList, setMaterialPriceList] = useState<MaterialPriceList>({} as MaterialPriceList);
    const [materialStates, setMaterialStates] = useState<MaterialGridInput[]>([]);
    const [isMaterialGridValid, setIsMaterialGridValid] = useState<boolean>(true);
    // Currency the loaded material price list is denominated in; null once loaded with none.
    const [priceListCurrency, setPriceListCurrency] = useState<string | null>();
    const currencyMismatch = getGridCurrencyMismatch('material', order.currency, currency, priceListCurrency);

    useEffect(() => {
        setMaterialStates(restorationMaterialsToGridInputs(restoration.materials, restoration.actions));
        return () => {
            setMaterialStates([]);
        };
        // Reset on another order or restoration only; a refetched copy of the same one keeps the edits.
    }, [order?._id, restoration._id]);

    useQuery(materialPriceListForOrderTemplate, {
        variables: { clientId: customerId },
        notifyOnNetworkStatusChange: true,
        fetchPolicy: 'no-cache',
        onCompleted(data) {
            setPriceListCurrency(data.materialPriceListForClient?.currency ?? null);
            if (data.materialPriceListForClient?.materials) {
                // Action-based orders take their currency from the lab connection, which the
                // actions grid reads off categorizedActions into the shared CurrencyContext.
                if (order.clinicOrderVersion === CONSTANTS.SETTINGS_VALUES.SIMPLE) {
                    if (!order.currency || order.currency === 'XXX') {
                        setCurrency(data.materialPriceListForClient.currency);
                    } else {
                        setCurrency(order.currency);
                    }
                }
                setMaterialPriceList(data.materialPriceListForClient);
            }
        },
        onError(error) {
            showBoundary(error);
        },
    });

    const [updateRestoration, { loading }] = useMutation(updateRestorationTemplate, {
        onCompleted: () => {
            if (onEditComplete) {
                onEditComplete();
            }
        },
        onError(error) {
            showBoundary(error);
        },
    });

    const handleChange = (materials: MaterialGridInput[], isGridValid: boolean) => {
        setMaterialStates((previous) => (isEqual(previous, materials) ? previous : materials));
        setIsMaterialGridValid(isGridValid);
    };

    const handleSaveClick = () => {
        updateRestoration({
            variables: {
                orderId: order._id,
                restoration: restorationWithGridRows(
                    restoration,
                    restorationActionsToGridInputs(restoration.actions),
                    materialStates,
                ),
            },
        });
    };

    const handleCancelClick = () => {
        if (onEditComplete) {
            onEditComplete();
        }
    };

    return (
        <Fragment>
            {shouldShowCurrencyMissingAlert(currency, priceListCurrency) && (
                <Box mb={1}>
                    <Alert severity="error" data-test="materials-data-grid-wrapper.currency-alert">
                        {translate('orders:ACTION_CURRENCY_MISSING')}
                    </Alert>
                </Box>
            )}
            {currencyMismatch && (
                <Box mb={1}>
                    <Alert severity="error" data-test="materials-data-grid-wrapper.currency-mismatch-alert">
                        {translate(`orders:${currencyMismatch.errorKey}`, currencyMismatch.values)}
                    </Alert>
                </Box>
            )}
            <MaterialsDataGrid
                materials={materialStates}
                priceList={materialPriceList}
                warranty={warranty}
                onChange={handleChange}
                allowMaterialMacros
            />
            <Box mt={1}>
                <LoadingButton
                    loading={loading}
                    size="small"
                    disabled={!isMaterialGridValid || !hasRealCurrency || !!currencyMismatch}
                    onClick={() => handleSaveClick()}
                    variant="contained"
                    sx={{ marginRight: 2 }}
                    data-test="materials-data-grid-wrapper.save-btn"
                >
                    {translate('common:SAVE')}
                </LoadingButton>
                <Button
                    size="small"
                    onClick={() => handleCancelClick()}
                    data-test="materials-data-grid-wrapper.cancel-btn"
                >
                    {translate('common:CANCEL')}
                </Button>
            </Box>
        </Fragment>
    );
};

export default MaterialsDataGridWrapper;
